import { CAMERA, clamp } from "@mucki/shared";

export interface PointerLook {
  yaw: number;
  pitch: number;
  isLocked(): boolean;
  requestLock(): void;
  releaseLock(): void;
  dispose(): void;
}

/**
 * Pointer lock drives camera yaw and pitch. Pitch is clamped away from the
 * poles so the camera cannot be flipped over the cat. Escape releases the lock
 * through the browser's own handling.
 */
export function createPointerLook(canvas: HTMLCanvasElement): PointerLook {
  const look = {
    yaw: 0,
    pitch: 0.35,
  };

  const onMouseMove = (event: MouseEvent) => {
    if (document.pointerLockElement !== canvas) return;
    look.yaw -= event.movementX * CAMERA.mouseSensitivity;
    look.pitch = clamp(
      look.pitch + event.movementY * CAMERA.mouseSensitivity,
      CAMERA.minPitch,
      CAMERA.maxPitch,
    );
  };

  const onClick = () => {
    if (document.pointerLockElement !== canvas) {
      // Chrome rejects the request while the previous exit is still settling.
      void Promise.resolve(canvas.requestPointerLock()).catch(() => undefined);
    }
  };

  document.addEventListener("mousemove", onMouseMove);
  canvas.addEventListener("click", onClick);

  return {
    get yaw() {
      return look.yaw;
    },
    get pitch() {
      return look.pitch;
    },
    isLocked: () => document.pointerLockElement === canvas,
    requestLock: onClick,
    releaseLock: () => {
      if (document.pointerLockElement === canvas) document.exitPointerLock();
    },
    dispose: () => {
      document.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("click", onClick);
    },
  };
}
